import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Button, ButtonGroup, Col, Label, Row } from 'reactstrap';
import { AvField, AvForm, AvGroup, AvInput } from 'availity-reactstrap-validation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IRootState } from 'app/shared/reducers';
import { createEntity, getEntity, getMasterEntities, reset, updateEntity } from './watotocare-entity.reducer';
import { hasAnyPermission } from 'app/shared/auth/permissions';
import { getAllByType as getCountries } from 'app/entities/region/region.reducer';
import { IRegion } from 'app/shared/model/region.model';
import { IWatotoCareEntityMaster } from 'app/shared/model/watotocare-entity-master.model';
import { useTranslation } from 'app/shared/util/translation-utils';

export interface IWatotoCareEntityUpdateProps extends StateProps, DispatchProps, RouteComponentProps<{
  id: string
}> {
}

export const WatotoCareEntityUpdate = (props: IWatotoCareEntityUpdateProps) => {
  const { t } = useTranslation();
  const [isNew, setIsNew] = useState(!props.match.params || !props.match.params.id);
  const {
    watotoCareEntityEntity,
    masterEntities,
    countries,
    userCountryId,
    loading,
    updating,
    hasUpdateAccess,
    currentLocale
  } = props;
  const [activeStatus, setActiveStatus] = useState(true);
  const [activeCountry, setActiveCountry] = useState<IRegion>({});
  const [activeMasterEntity, setActiveMasterEntity] = useState<IWatotoCareEntityMaster>({});
  const [regCountriesList, setRegCountriesList] = useState<IRegion[]>([]);
  const masterEntityId = new URLSearchParams(props.location.search).get('masterEntityId') ?? null;

  const handleClose = () => {
    props.history.goBack();
  };

  useEffect(() => {
    if (isNew) {
      props.reset();
    } else {
      props.getEntity(props.match.params.id);
    }
  }, []);

  useEffect(() => {
    props.getCountries();
    props.getMasterEntities();
  }, []);

  useEffect(() => {
    if ((isNew || watotoCareEntityEntity.id) && userCountryId && countries.length > 0 && masterEntities.length > 0) {
      let ac: IRegion;
      if (isNew) {
        ac = countries.find(country => country.id === (props.isAdmin ? countries[0].id : props.userCountryId));
      } else {
        ac = countries.find(country => country.id === watotoCareEntityEntity?.countryId);
      }
      if (ac) {
        setActiveCountry(ac);
      }

      const allowedCountries = countries.filter((ele) => {
        return props.isAdmin ? 1 : ele.id === userCountryId;
      });
      setRegCountriesList(allowedCountries);

      if (masterEntityId) {
        const ame = masterEntities.find(masterEntity => masterEntity.id === masterEntityId);
        if (ame) {
          setActiveMasterEntity(ame);
        }
      } else {
        setActiveMasterEntity(masterEntities[0]);
      }
    }
  }, [countries, userCountryId, watotoCareEntityEntity, masterEntities]);

  const onCountryChange = (event) => {
    const countryId = event.target.value;
    const newActiveCountry = countries.find(country => country.id === countryId);
    setActiveCountry(newActiveCountry);
  };

  const onMasterEntityChange = (event) => {
    const newMasterEntityId = event.target.value;
    const newMasterEntity = masterEntities.find(masterEntity => masterEntity.id === newMasterEntityId);
    setActiveMasterEntity(newMasterEntity);
  };

  useEffect(() => {
    if (!isNew) {
      setActiveStatus(watotoCareEntityEntity.isActive);
    }
  }, [watotoCareEntityEntity]);

  useEffect(() => {
    if (props.updateSuccess) {
      handleClose();
    }
  }, [props.updateSuccess]);

  const saveEntity = (event, errors, values) => {
    if (errors.length === 0) {
      const entity = {
        ...watotoCareEntityEntity,
        ...values,
        countryId: activeCountry.id,
        isActive: activeStatus
      };

      if (isNew) {
        props.createEntity(entity);
      } else {
        props.updateEntity(entity);
      }
    }
  };


  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="watotocareApp.watotocare.entity.home.createOrEditLabel">
            {isNew ? t('watotocareEntity.update.addTitle') : t('watotocareEntity.update.editTitle')}
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>{t('common.loading')}</p>
          ) : (
            <AvForm model={isNew ? {} : watotoCareEntityEntity} onSubmit={saveEntity}>
              <br />
              {!isNew ? (
                <AvGroup>
                  <Label for="watotocare-entity-id">{t('common.fields.id')}</Label>
                  <AvInput id="watotocare-entity-id" type="text" className="form-control" name="id" required
                           readOnly />
                </AvGroup>
              ) : null}

              <br />
              <div className="mt-3">
                <Row className="justify-content-left">
                  <Col md="6">
                    <AvGroup>
                      <Label id="isActive" for="watotocare-entity-isActive">
                        {t('common.fields.activeStatus')}
                      </Label>
                      <ButtonGroup style={{ marginLeft: '30px' }}>
                        <Button
                          color={activeStatus ? 'primary' : 'secondary'}
                          onClick={(e) => {
                            setActiveStatus(true);
                          }}
                        >
                          {t('common.status.active')}
                        </Button>

                        <Button
                          color={!activeStatus ? 'primary' : 'secondary'}
                          onClick={() => {
                            setActiveStatus(false);
                          }}
                        >
                          {t('common.status.inactive')}
                        </Button>
                      </ButtonGroup>
                    </AvGroup>
                  </Col>
                </Row>

                <br />
                <Row className="justify-content-left">
                  <Col md="6">
                    <AvGroup>
                      <Label id="enDescription" for="watotoCareEntity-enDescription">
                        {t('watotocareEntity.fields.enDescription')}
                      </Label>
                      <AvField
                        id="watotoCareEntity-enDescription"
                        type="text"
                        name="enDescription"
                        disabled={!hasUpdateAccess}
                        validate={{
                          required: { value: true, errorMessage: t('common.validation.required') }
                        }}
                      />
                    </AvGroup>
                  </Col>
                  <Col md="6">
                    <AvGroup>
                      <Label id="stDescription" for="watotoCareEntity-stDescription">
                        {t('watotocareEntity.fields.stDescription')}
                      </Label>
                      <AvField
                        id="watotoCareEntity-stDescription"
                        type="text"
                        name="stDescription"
                        disabled={!hasUpdateAccess}
                        validate={{
                          required: { value: true, errorMessage: t('common.validation.required') }
                        }}
                      />
                    </AvGroup>
                  </Col>
                  <Col md="6">
                    <AvGroup>
                      <Label for="watotocare-entity-country">{t('common.fields.country')}</Label>
                      <AvInput
                        id="watotocare-entity-country"
                        type="select"
                        className="form-control"
                        name="countryId"
                        disabled={!isNew}
                        value={isNew ? activeCountry?.id : watotoCareEntityEntity?.countryId}
                        onChange={onCountryChange}>
                        {regCountriesList
                          && (props.isAdmin ? regCountriesList.map(country => (
                            <option value={country.id} key={country.id}>
                              {country.name}
                            </option>
                          )) : regCountriesList.map(country => (
                            props.userCountryId === country.id && <option value={country.id} key={country.id}>
                              {country.name}
                            </option>
                          )))}
                      </AvInput>
                    </AvGroup>
                  </Col>
                  <Col md="6">
                    <AvGroup>
                      <Label id="displayOrder" for="watotocare-entity-displayOrder">
                        {t('watotocareEntity.fields.displayOrder')}
                      </Label>
                      <AvField
                        id="watotocare-entity-displayOrder"
                        type="number"
                        name="displayOrder"
                        disabled={!hasUpdateAccess}
                        validate={{
                          required: { value: true, errorMessage: t('common.validation.required') }
                        }}
                      />
                    </AvGroup>
                  </Col>
                  <Col md="6">
                    <AvGroup>
                      <Label for="watotocare-entity-master">{t('watotocareEntity.fields.masterEntity')}</Label>
                      <AvInput
                        id="watotocare-entity-master"
                        type="select"
                        className="form-control"
                        name="entityMasterId"
                        disabled={!isNew}
                        value={isNew ? activeMasterEntity?.id : watotoCareEntityEntity?.entityMasterId}
                        onChange={onMasterEntityChange}>
                        {masterEntities
                          && (props.isAdmin ? masterEntities.map(masterEntity => {
                              const displayName = currentLocale !== 'en' && masterEntity.stEntityName ? 
                                masterEntity.stEntityName : 
                                masterEntity.entityName;
                              return (
                                <option value={masterEntity.id} key={masterEntity.id}>
                                  {displayName}
                                </option>
                              );
                            }) : regCountriesList.map(country => (
                            props.userCountryId === country.id && <option value={country.id} key={country.id}>
                              {country.name}
                            </option>
                          )))}
                      </AvInput>
                    </AvGroup>
                  </Col>
                </Row>
              </div>

              <br />
              <Button tag={Link} id="cancel-save" onClick={handleClose} replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">{t('common.actions.back')}</span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" type="submit" disabled={!hasUpdateAccess || updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp; {t('common.actions.save')}
              </Button>
            </AvForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

const mapStateToProps = (storeState: IRootState) => ({
  watotoCareEntityEntity: storeState.watotoCareEntity.entity,
  loading: storeState.watotoCareEntity.loading,
  updating: storeState.watotoCareEntity.updating,
  updateSuccess: storeState.watotoCareEntity.updateSuccess,
  masterEntities: storeState.watotoCareEntity.masterEntities,
  countries: storeState.region.countries,
  userCountryId: storeState.authentication.userCountryId,
  hasUpdateAccess: hasAnyPermission(storeState.authentication.permissions, ['ADMIN', 'ENTITIES_MODIFY']),
  isAdmin: hasAnyPermission(storeState.authentication.account.roles, ['ADMIN']),
  currentLocale: storeState.locale.currentLocale
});

const mapDispatchToProps = {
  getEntity,
  updateEntity,
  createEntity,
  getCountries,
  getMasterEntities,
  reset
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(WatotoCareEntityUpdate);
